import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdCenterMiniCardComponent } from '../basic-properties/hd-center-mini-card.component';
import { HD_BODYGRAPH_GATES_BY_CENTER } from '../hd-bodygraph-metadata';
import { HdBodygraphActivation, HdBodygraphCenter, HdBodygraphRenderState } from '../hd-bodygraph.models';
import { HdContentLanguageService } from '../hd-content-language.service';

export interface HdCenterCheatSheetAnchor {
  readonly left: number;
  readonly right: number;
  readonly centerY: number;
}

export interface HdCenterCheatSheetGeometry {
  readonly anchors: Readonly<Partial<Record<HdBodygraphCenter, HdCenterCheatSheetAnchor>>>;
}

interface CenterCheatSheetAnnotation {
  readonly center: HdBodygraphCenter;
  readonly side: 'left' | 'right';
  readonly verticalOffset: number;
  readonly horizontalOffset: number;
  readonly width: number;
  readonly themeOnOwnLine: boolean;
  readonly variant: 'defined' | 'undefined';
  readonly isOpen: boolean;
}

interface CenterCheatSheetPlacement {
  readonly center: HdBodygraphCenter;
  readonly side: 'left' | 'right';
  readonly verticalOffset: number;
  /**
   * Extra horizontale verschuiving (px) t.o.v. de centerrand, in schermrichting:
   * negatief schuift naar links, positief naar rechts — ongeacht de zijde.
   */
  readonly horizontalOffset?: number;
  /**
   * Tekstbreedte (px) van de annotatie. Bewust per center: de annotaties werken
   * in de hoogte, dus elk blokje is net zo smal als de ruimte naast dat center
   * toelaat (sacraal past tussen milt-shape en sacraal-shape, ajna/milt hebben
   * de kanalen naast zich, emotie heeft de meeste vrije ruimte).
   */
  readonly width: number;
  /** Zet het Not-Self thema op een eigen regel i.p.v. inline vóór de eerste zin. */
  readonly themeOnOwnLine?: boolean;
}

const CENTER_PLACEMENTS: readonly CenterCheatSheetPlacement[] = [
  { center: 'ajna', side: 'left', verticalOffset: -12, width: 130 },
  { center: 'zelf', side: 'left', verticalOffset: -8, width: 140, themeOnOwnLine: true },
  { center: 'milt', side: 'left', verticalOffset: -24, width: 120 },
  { center: 'sacraal', side: 'left', verticalOffset: 26, width: 95 },
  { center: 'hoofd', side: 'right', verticalOffset: 10, width: 190, themeOnOwnLine: true },
  { center: 'keel', side: 'right', verticalOffset: -4, width: 150 },
  { center: 'hart', side: 'right', verticalOffset: -18, width: 160, horizontalOffset: -20 },
  { center: 'solarplexus', side: 'right', verticalOffset: 24, width: 210 },
  { center: 'root', side: 'right', verticalOffset: 18, width: 180 },
];

@Component({
  selector: 'hd-center-cheat-sheet-layer',
  imports: [HdCenterMiniCardComponent],
  templateUrl: './hd-center-cheat-sheet-layer.component.html',
  styleUrl: './hd-center-cheat-sheet-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-test-id': 'center-cheat-sheet-layer',
  },
})
export class HdCenterCheatSheetLayerComponent {
  readonly renderState = input.required<HdBodygraphRenderState>();
  readonly activations = input<readonly HdBodygraphActivation[]>([]);
  readonly geometry = input<HdCenterCheatSheetGeometry | null>(null);

  private readonly transloco = inject(TranslocoService);
  private readonly contentLanguage = inject(HdContentLanguageService);

  protected readonly annotations = computed<readonly CenterCheatSheetAnnotation[]>(() =>
    CENTER_PLACEMENTS.map(placement => {
      const isDefined = this.renderState().centerStates[placement.center].isDefined;
      const hasActivation = this.activations().some(activation =>
        HD_BODYGRAPH_GATES_BY_CENTER[placement.center].includes(activation.gate),
      );
      return {
        ...placement,
        horizontalOffset: placement.horizontalOffset ?? 0,
        themeOnOwnLine: placement.themeOnOwnLine === true,
        variant: isDefined ? 'defined' : 'undefined',
        isOpen: !isDefined && !hasActivation,
      };
    }),
  );

  protected readonly label = computed(() => {
    this.contentLanguage.translationsLoadedTick();
    return this.transloco.translate(
      'humanDesign.chart.shortcutBar.centerCheatSheetAria',
      undefined,
      this.contentLanguage.effectiveLang(),
    );
  });

  protected annotationX(
    annotation: CenterCheatSheetAnnotation,
    anchor: HdCenterCheatSheetAnchor,
  ): number {
    const edge = annotation.side === 'left' ? anchor.left : anchor.right;
    return edge + annotation.horizontalOffset;
  }

  protected annotationY(
    annotation: CenterCheatSheetAnnotation,
    anchor: HdCenterCheatSheetAnchor,
  ): number {
    return anchor.centerY + annotation.verticalOffset;
  }
}
